import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Calendar } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/articles`);
        setArticles(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const categories = ['All', 'Fashion', 'Beauty', 'Planning', 'Inspiration', 'Tips', 'Lifestyle'];
  
  const filteredArticles = selectedCategory === 'All' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title="Tips & Inspiration"
        description="Expert wedding planning tips, fashion inspiration, beauty advice, and Mediterranean wedding ideas from Malta's premier bridal magazine."
      />
      {/* Hero Section with Image */}
      <section className="relative">
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1600&h=800&fit=crop"
            alt="Wedding inspiration and planning"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FAF9F7]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-white/90 text-xs tracking-[0.3em] uppercase mb-4">
                TIPS & INSPIRATION
              </p>
              <h1 
                className="text-4xl md:text-6xl text-white mb-4 tracking-tight drop-shadow-lg"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Wedding Inspiration
              </h1>
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-8">
          <div className="w-20 h-0.5 bg-[#DE92A9] mx-auto mb-6"></div>
          <p className="text-[#6B4E37] text-lg leading-relaxed">
            Expert advice, planning guides, fashion trends, beauty tips, and insider inspiration to help you navigate every step of your wedding journey.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-[#F5F3F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded text-xs tracking-wider transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-[#DE92A9] text-white'
                    : 'bg-[#F5F3F0] text-[#6B4E37] hover:bg-[#d4c4b0]'
                }`}
              >
                {category.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-[#8B7D6B]">Loading articles...</div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center text-[#8B7D6B] py-12">
              <p className="text-lg">No articles found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link 
                  key={article.id} 
                  to={`/tips-inspiration/${article.slug || article.id}`}
                  data-testid={`article-card-${article.slug || article.id}`}
                >
                  <Card className="group overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-white h-full">
                    {/* Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <Badge className="absolute top-4 left-4 bg-[#DE92A9] text-white border-none">
                        {article.category}
                      </Badge>
                      
                      {/* Read Article Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                        <span className="bg-white/90 text-[#2C2C2C] px-4 py-2 rounded text-sm tracking-wider flex items-center gap-2">
                          READ ARTICLE <ArrowRight size={14} />
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 text-xs text-[#8B7D6B] mb-3">
                        <Calendar size={14} />
                        <span>{article.date}</span>
                      </div>

                      <h3 className="text-xl text-[#2C2C2C] mb-3 leading-tight group-hover:text-[#DE92A9] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      
                      <p className="text-[#6B4E37] text-sm leading-relaxed line-clamp-3 mb-4">
                        {article.excerpt}
                      </p>

                      <div className="flex items-center text-[#DE92A9] text-sm font-medium group-hover:gap-2 transition-all">
                        Read More
                        <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
